import { useState } from 'react';
import { cancelColor, confirmColor, confirmBorderColor } from '../constants/Styles';
import { DRNoteInput } from '../components/DRControls';
import { HorizontalButtonPairView } from '../components/DRViewUtils';
import StoragePlusLoadingView from '../components/StoragePlusLoadingView';
import { joinCommaSeparatedNames } from './utils/composeText';
import './LoadItemsScreen.scss';
import { LocktubeData } from '../constants/containerManagerInterfaces';
import CornerGripperLogo from '../components/CornerGripperLogo';
import Dialog from '../components/Dialog';
import { getConfig } from '../utils/configUtils';
import RequestScreen from './RequestScreen';
import { Dropoff } from '../types/taskInterfaces';
import { Task } from '../constants/taskInterfaces';
import { r2h, getUnitName, countForReload } from '../vodjs/lib';
import LoadCancelScreen from './LoadCancelScreen';
import { elasticLogError } from '../vodjs/ElasticLog';
import { allowMulti } from '../vodjs/Utils';

interface LoadItemsScreenProps {
  clickCancel: () => void;
  clickConfirm: () => void;
  confirmDisabled: boolean;
  dropoffNames: string[];
  loadInstructions: string;
  storageState: LocktubeData; // TODO Mod to not need whole object
  toggleStorageSelection: (tubeId: string) => void;
  noteText: string;
  noteOnChange: (value: string) => void;
  requestedBy: string;
  tapToReload: () => void;
  hideDetails: boolean;
  isRestricted: boolean;
  continueButtonText?: string;
  codeRequired?: boolean;
  autoPrioConfig?:any;
  getOpenHoursInfo:(stationCode:String) => void;
  destinations: any[];
  taskData: Task;
  getEnabledKioskFriendlyNames:() => String[];
  getDrawersLoadedForCurrentTask:() => String[];
  onClickCancelPendingUnload:() => void;
  taskUuid: string;
}

export const LOAD_PROMPTS = {
  selectStorage: `1. Select one or more available drawers.${'\n'}2. Scan your badge to unlock them.`,
  closeStorage: 'Drawers have been opened. Close them to continue.',
};

export default function LoadItemsScreen(props: LoadItemsScreenProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [onBotEditCtx, setOnBotEditCtx] = useState<any>(undefined);
  const [showUnloadDialog, setShowUnloadDialog] = useState(false);

  const {
    clickCancel,
    clickConfirm,
    confirmDisabled,
    dropoffNames,
    loadInstructions,
    storageState,
    toggleStorageSelection,
    noteText,
    noteOnChange,
    requestedBy,
    tapToReload,
    hideDetails,
    isRestricted,
    continueButtonText,
    codeRequired,
    autoPrioConfig,
    getOpenHoursInfo,
    destinations,
    taskData,
    getEnabledKioskFriendlyNames,
    getDrawersLoadedForCurrentTask,
    onClickCancelPendingUnload,
    taskUuid
  } = props;

  //Prohibit stop changes if any drawer is either unlocked or filled:
  // - If such state was caused by this batch load, we're past ammendability
  // - If it was an unrelated task gone bad leaving remnant state, we  
  //     choose not to burn dev cycles for that rogue case.
  const drawersAllowStopChanges = () =>  {
    return !Object.keys(storageState.tubes).some(d => {
      const tube = storageState.tubes[d];
      return tube.unlocked || tube.filled;
    });
  };


  const beginEditing = () => {

    //Unmark the drawers. This serves two purposes:
    //  1. Prevents drawers opening due to badge swipes while editing, and
    //  2. If the nurse indeed changes the batch, prevents the drawers 
    //     from being marked at the get-go of the replacement batch.
    for(let i = 0; i < destinations.length; ++i) {
      if(destinations[i].drawer)
        toggleStorageSelection(destinations[i].drawer);
    }

    let restricted = false; //derive this from orig tasks
    let codeRequired = false;
    let test = false;

    let task = taskData;
    let {start_goal, end_goal} = task.task_level_args;
    if(task.task_data.restricted)
      restricted = true;  //overall batch
    if(task.task_data.code_required)
      codeRequired = true;
    if(task.task_data.test)
      test = true;

    let origBatch = [{
      id:0,
      startGoal:r2h(start_goal.value),
      endGoal:r2h(end_goal.value),
      note:noteText || ''
    }];


    let m1FriendlyPickup = origBatch[0].startGoal;
    let endGoals = getEnabledKioskFriendlyNames();
    endGoals = endGoals.filter(friendlyName => friendlyName !== m1FriendlyPickup);
    endGoals.sort();

    let editedBatch = structuredClone(origBatch);

    setOnBotEditCtx({endGoals, origBatch, editedBatch, restricted, origRestricted:restricted, codeRequired, test});
  }

  const clickCancelAndUnload = () => {
    let drawersToUnload = getDrawersLoadedForCurrentTask();
    
    // the cases are split like this so that we can run func clickCancel() again when the drawers are unloaded
    if(drawersToUnload.length > 0) {
      onClickCancelPendingUnload();
      setShowUnloadDialog(true);
    }
    else {
      clickCancel();
    }
  }

  let task = taskData;

  if(!task && allowMulti('noLoadTask', 1, 5))
    elasticLogError({what:'noLoadTask', taskUuid, msg:`No task data for load of ${taskUuid}`});

  let showLoadEdits = false;
  if(task) {
    const scheduled = task.task_data.scheduled;  
    showLoadEdits = !!autoPrioConfig?.enableLoadEdits && !scheduled;
  }

  const width = Math.round((100 - 15) / (showLoadEdits ? 3 : 2)) + '%';

  const loadButtonSettings = [
    {
      phrase: 'CANCEL',
      borderColor: cancelColor,
      fillColor: 'transparent',
      fontColor: cancelColor,
      action: () => setShowCancelDialog(true),
      width
    },
    showLoadEdits ? {
        phrase: 'CHANGE STOPS',
        fillColor: confirmColor,
        borderColor: confirmBorderColor,
        fontColor: 'white',
        action: () => beginEditing(),
        disabled:!!taskData && !drawersAllowStopChanges(),
        buttonStyling:{fontSize:30},
        width
      } : undefined,
    {
      phrase: continueButtonText || 'DELIVER',
      fillColor: confirmColor,
      borderColor: confirmBorderColor,
      fontColor: 'white',
      action: clickConfirm,
      disabled: confirmDisabled,
      width
    }
  ].filter(Boolean);


  const supportNumber = getConfig().site?.supportNumber;

  if(onBotEditCtx) {

    return <RequestScreen
      disableStartGoalSelection={true}
      endGoals={onBotEditCtx.endGoals}
      defaultStartGoal={onBotEditCtx.origBatch[0].startGoal}
      closeModal={() => setOnBotEditCtx(undefined)}
      clickRequest={() => {}} //setConfirmVisible(true)
      dropoffs={onBotEditCtx.editedBatch}
      setDropoffs={(editedBatch: Dropoff[]) => setOnBotEditCtx({...onBotEditCtx, editedBatch})}
      isRestricted={!!onBotEditCtx.restricted} 
      setRestricted={(value: boolean) => setOnBotEditCtx({...onBotEditCtx, restricted: value, codeRequired: !value ? false : onBotEditCtx.codeRequired})}
      isCodeRequired={!!onBotEditCtx.codeRequired}
      setPinRequired={(value: boolean) => setOnBotEditCtx({...onBotEditCtx, codeRequired: value})}
      showRestricted={true} //this.state.showRestricted
      noKioskText={`Please call Moxi support`}  //this.noKioskText()
      getOpenHoursInfo={(stationCode:string) => getOpenHoursInfo(stationCode)}
      onBotEditCtx={onBotEditCtx}
      autoPrioConfig={autoPrioConfig}
      drawersAllowStopChanges={drawersAllowStopChanges()}
      noteOnChange={noteOnChange}
      isTest={onBotEditCtx.test}
    />
  }

  const enableLoadCancel = !!autoPrioConfig?.enableLoadCancel;
  let cancelFunc = enableLoadCancel ? clickCancelAndUnload : clickCancel;
  const cancelText = `Moxi support ${
    supportNumber ? `(${supportNumber}) ` : ''
  }could help resolve an issue without canceling the task. Are you sure you want to cancel this task?`;

  if(showUnloadDialog && enableLoadCancel){
    return (
      <LoadCancelScreen
        tapToReload={() => countForReload({ label: 'LoadCancel' })}
        lastScanTimestamp={storageState.lastScanTimestamp}
        scanResult={storageState.scanResult}
        drawerStatus={storageState.status}
        clickCancel={clickCancel}
        readyToCancel={storageState.readyToCancel}
        taskUuid = {taskUuid}
      />
    );
  }

  if(!task) {
    return (
      <>
        <div className="load-items-screen">
          <CornerGripperLogo tapToReload={tapToReload} />
          <div 
            style={{display:'flex', flexFlow:'column', alignItems:'center', justifyContent:'center',
              width:'100%', height:'100%', backgroundColor:'#47b7b9', color:'white'}}
            >
            <div style={{fontSize:50}}>Please call Moxi support</div>
            {!!supportNumber && <div style={{fontSize:30}}>{supportNumber}</div>}
            {!!taskUuid && <div style={{fontSize:16}}>{`Task id: ${taskUuid}`}</div>}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {showCancelDialog && (
        <Dialog
          title="Cancel This Task?"
          text={cancelText}
          primaryButtonText="CANCEL TASK"
          primaryButtonColor={cancelColor}
          clickPrimaryButton={cancelFunc}
          secondaryButtonText="GO BACK"
          secondaryButtonColor={confirmBorderColor}
          clickSecondaryButton={() => setShowCancelDialog(false)}
        />
      )}
      <div className="load-items-screen">
        <CornerGripperLogo tapToReload={tapToReload} />
        <div className="load-items-screen__header">
          <div className="header__title-wrapper">
            {hideDetails ? (
              <span className="header__title-text">Load Moxi for Delivery</span>
            ) : (
              <>
                <span className="header__title-text">Load Moxi for </span>
                <span className="header__title-text--emph">{joinCommaSeparatedNames(dropoffNames)}</span>
              </>
            )}
          </div>
          {requestedBy && (
            <div className="header__requested-by-wrapper">
              <span className="header__requested-by-text">Requested By </span>
              <span className="header__requested-by-text--emph">{requestedBy}</span>
            </div>
          )}
          <div className="header__load-prompt-wrapper">
            <span className="header__load-prompt-text">{loadInstructions}</span>
          </div>
        </div>
        <div className="load-items-screen__content">
          <StoragePlusLoadingView locktubes={storageState} toggleTubeSelected={toggleStorageSelection} />
          <div className="content__note-wrapper">
            {isRestricted && (
              <div className="note-wrapper__header">{codeRequired ? 'Multi-factor' : 'Restricted'} Delivery</div>
            )}
            <DRNoteInput
              textInputStyle={{ height: '100%', width: '100%' }}
              onChangeText={noteOnChange}
              value={noteText}
              placeholderText={'Ex: “Telemetry box for rm#101”'}
            />
          </div>
        </div>
        <div className="load-items-screen__footer">
          <HorizontalButtonPairView buttonSettings={loadButtonSettings} />
        </div>
      </div>
    </>
  );
}
